import { createNodeRuleSet, createShapesContainerNode, createSilkShapesContainerNode, createTextShapeUnderShapesContainer, FootPrintPadHoleType, FootPrintPadShape, TextAlignment, } from "@buildwithflux/models";
import { metersToMm } from "@buildwithflux/shared";
// eslint-disable-next-line boundaries/element-types
import { PcbLayoutRuleCompiler } from "../../pcb/layout_engine/rule_parsing_and_compilation/PcbLayoutRuleCompiler";
import { TextPlaceholder } from "../../SharedDataModels";
import { replaceAutoWithDefault2, replaceAutoWithDefault3 } from "../utils";
import { designatorFontSize, designatorMargin, pinMarkerMargin, pinMarkerRadius, silkWidth } from "./commonConstants";
/**
 * TO package configurations
 * All dimensions in millimeters (will be converted to meters)
 */
export const toPackageConfigs = {
    "TO-92": {
        pinCount: 3,
        pinPitch: 1.27,
        pinSizeX: 0.8,
        pinSizeY: 0.8,
        packageWidth: 4.5,
        packageHeight: 4.0,
        packageDepth: 3.5,
        holeDiameter: 0.7,
        isSMD: false,
        hasTab: false,
    },
    "TO-220": {
        pinCount: 3,
        pinPitch: 2.54,
        pinSizeX: 1.4,
        pinSizeY: 1.4,
        packageWidth: 10.0,
        packageHeight: 15.0,
        packageDepth: 4.5,
        holeDiameter: 1.0,
        isSMD: false,
        hasTab: true,
        tabWidth: 10.0,
        tabHeight: 8.0,
    },
    "TO-252": {
        // DPAK
        pinCount: 3,
        pinPitch: 2.3,
        pinSizeX: 0.9,
        pinSizeY: 1.5,
        packageWidth: 6.5,
        packageHeight: 6.1,
        packageDepth: 2.3,
        holeDiameter: 0,
        isSMD: true,
        hasTab: true,
        tabWidth: 5.3,
        tabHeight: 5.2,
    },
    "TO-263": {
        // D2PAK
        pinCount: 3,
        pinPitch: 2.54,
        pinSizeX: 1.0,
        pinSizeY: 2.0,
        packageWidth: 10.0,
        packageHeight: 8.9,
        packageDepth: 4.6,
        holeDiameter: 0,
        isSMD: true,
        hasTab: true,
        tabWidth: 8.0,
        tabHeight: 6.0,
    },
};
/**
 * Get TO package config from package name
 */
export function getToConfig(packageName) {
    return toPackageConfigs[packageName];
}
function toInferPinLayout(bakedRules, existingPadNodes) {
    const result = { grid: [], otherPads: [] };
    const withoutPinNumberPadNodes = existingPadNodes.filter((pad) => pad.terminalId === "" || pad.terminalId === undefined);
    withoutPinNumberPadNodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
    const remainingPadsWithNumbers = existingPadNodes.filter((pad) => pad.terminalId !== "" && pad.terminalId !== undefined && pad.terminalId !== "TAB");
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    // TO packages have pins in a single row
    for (let i = 0; i < gridX; i++) {
        const foundMatchingI = remainingPadsWithNumbers.findIndex((pad) => pad.terminalId === `${i + 1}`);
        const foundMatching = remainingPadsWithNumbers[foundMatchingI];
        if (foundMatchingI !== -1) {
            remainingPadsWithNumbers.splice(foundMatchingI, 1);
        }
        const matchingPad = foundMatching ?? withoutPinNumberPadNodes.shift();
        const sideArr = result.grid[0] ?? [];
        sideArr.push(matchingPad ?? null);
        result.grid[0] = sideArr;
    }
    // Add tab pad to otherPads if it exists
    const tabPad = existingPadNodes.find((pad) => pad.terminalId === "TAB");
    if (tabPad) {
        result.otherPads.push(tabPad);
    }
    result.otherPads.push(...withoutPinNumberPadNodes);
    result.otherPads.push(...remainingPadsWithNumbers);
    return result;
}
export function toComputeInferredRules(bakedRules, config, existingPadNodes) {
    const pinSize = replaceAutoWithDefault2(bakedRules.pinSize, { x: config.pinSizeX, y: config.pinSizeY });
    const pinPitch = replaceAutoWithDefault2(bakedRules.pinPitch, { x: config.pinPitch, y: 0 });
    // Filter out tab pad when counting pins
    const pinsMinusTab = existingPadNodes.filter((pad) => pad.terminalId !== "TAB");
    const pinsCount = pinsMinusTab.length;
    const gridArrayCount = replaceAutoWithDefault2(bakedRules.gridArrayCount, {
        x: config.gridArrayCountX ?? pinsCount,
        y: 0, // TO packages have pins in a single row
    });
    const packageSize = replaceAutoWithDefault3(bakedRules.packageSize, {
        x: config.packageWidth,
        y: config.packageDepth,
        z: config.packageHeight,
    });
    const newBakedRules = {
        ...bakedRules,
        packageSize,
        pinSize,
        pinPitch,
        gridArrayCount,
    };
    return { ...newBakedRules, pinLayout: toInferPinLayout(newBakedRules, existingPadNodes) };
}
export function toGeneratePadRules(footprintNodeUid, bakedRules, config) {
    const result = {};
    const pinSizeX = bakedRules.pinSize?.x ?? 0;
    const pinSizeY = bakedRules.pinSize?.y ?? 0;
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const totalX = (gridX - 1) * pinPitchX;
    const packageSizeY = bakedRules.packageSize?.y ?? 0;
    const pinLayout = bakedRules.pinLayout;
    if (!pinLayout)
        return result;
    // Process main pins (single row)
    const sideArr = pinLayout.grid[0];
    if (sideArr) {
        for (let i = 0; i < sideArr.length; i++) {
            const matchingPad = sideArr[i];
            if (!matchingPad)
                continue;
            const posX = i * pinPitchX - totalX / 2;
            const posY = -packageSizeY / 2 + pinSizeY / 2;
            const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(matchingPad.type, matchingPad.uid);
            const ruleSet = {
                uid: matchingPad.uid,
                selector: selector,
                rules: {},
                generatedFromAncestorNodeUid: footprintNodeUid,
            };
            result[ruleSet.uid] = ruleSet;
            const rules = {
                position: `${metersToMm(posX)}mm ${metersToMm(posY)}mm`,
                size: `${metersToMm(pinSizeX)}mm ${metersToMm(pinSizeY)}mm`,
            };
            if (config.isSMD) {
                rules.padHoleType = FootPrintPadHoleType.SurfaceMountDevice;
                rules.padShape = FootPrintPadShape.rectangle;
            }
            else {
                rules.padHoleType = FootPrintPadHoleType.platedThroughHole;
                rules.holeSize = `${metersToMm(config.holeDiameter)}mm`;
            }
            ruleSet.rules = createNodeRuleSet(rules);
        }
    }
    // Handle tab pad if present
    const tabPad = pinLayout.otherPads.find((pad) => pad.terminalId === "TAB");
    if (tabPad && bakedRules.thermalPadSize) {
        const selector = PcbLayoutRuleCompiler.createUidAttributeSelector(tabPad.type, tabPad.uid);
        const ruleSet = {
            uid: tabPad.uid,
            selector: selector,
            rules: {},
            generatedFromAncestorNodeUid: footprintNodeUid,
        };
        result[ruleSet.uid] = ruleSet;
        const rules = {
            position: `0mm ${metersToMm(packageSizeY / 4)}mm`,
            size: `${metersToMm(bakedRules.thermalPadSize.x)}mm ${metersToMm(bakedRules.thermalPadSize.y)}mm`,
            padShape: FootPrintPadShape.rectangle,
            padHoleType: FootPrintPadHoleType.SurfaceMountDevice,
        };
        ruleSet.rules = createNodeRuleSet(rules);
    }
    return result;
}
export function toGenerateAdditionalPads(_footprintNodeUid, _bakedRules) {
    return [];
}
export function toGenerateSilkNodes(footprintNodeUid, bakedRules) {
    if (!bakedRules.packageSize)
        return [];
    const gridX = bakedRules.gridArrayCount?.x ?? 0;
    const pinPitchX = bakedRules.pinPitch?.x ?? 0;
    const totalX = (gridX - 1) * pinPitchX;
    const result = [];
    const shapesContainerNode = createShapesContainerNode(footprintNodeUid);
    result.push(shapesContainerNode);
    const silkPaths = [];
    const packageWidth = bakedRules.packageSize.x;
    const packageHeight = bakedRules.packageSize.y;
    // Draw package outline
    silkPaths.push(`<path d="
              M ${-packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${-packageHeight / 2}
              L ${packageWidth / 2} ${packageHeight / 2}
              L ${-packageWidth / 2} ${packageHeight / 2}
              Z" stroke-width="${silkWidth}"
              stroke-linecap="round" fill="none" />`);
    // Pin 1 marker
    const pin1X = -totalX / 2;
    silkPaths.push(`<circle
              cx="${pin1X}"
              cy="${-packageHeight / 2 - pinMarkerMargin}"
              r="${pinMarkerRadius}" fill="#000000" />`);
    const svgNode = createSilkShapesContainerNode(shapesContainerNode.uid, footprintNodeUid, silkPaths);
    result.push(svgNode);
    const designatorTextNode = createTextShapeUnderShapesContainer("Designator", shapesContainerNode.uid, `${shapesContainerNode.uid}_designator`, footprintNodeUid);
    designatorTextNode.pcbNodeRuleSet = createNodeRuleSet({
        position: `${metersToMm(0)}mm ${metersToMm(packageHeight / 2 + designatorMargin)}mm`,
        textAlign: TextAlignment.center,
        content: `= ${TextPlaceholder.elementDesignator}`,
        fontSize: `${metersToMm(designatorFontSize)}mm`,
    }, true);
    result.push(designatorTextNode);
    return result;
}
//# sourceMappingURL=to.js.map